import React from 'react';
import { clsx } from 'clsx';
import type { LucideIcon } from 'lucide-react';
import styles from './Callout.module.css';

export type CalloutVariant =
  | 'info'
  | 'warning'
  | 'success'
  | 'error'
  | 'accent'
  | 'purple'
  | 'neutral';

interface CalloutProps {
  /** Optional leading icon (lucide). */
  icon?: LucideIcon;
  /** Optional bold title above the body text. */
  title?: React.ReactNode;
  /** Main content. Can be a string or rich nodes. */
  children: React.ReactNode;
  /** Visual variant. Defaults to 'info'. */
  variant?: CalloutVariant;
  /** Optional action slot (buttons / links) rendered on the right. */
  actions?: React.ReactNode;
  /** Click handler — promotes the callout to a button role. */
  onClick?: () => void;
  className?: string;
}

const VARIANT_CLASS: Record<CalloutVariant, string> = {
  info: styles.variantInfo,
  warning: styles.variantWarning,
  success: styles.variantSuccess,
  error: styles.variantError,
  accent: styles.variantAccent,
  purple: styles.variantPurple,
  neutral: styles.variantNeutral,
};

export function Callout({
  icon: Icon,
  title,
  children,
  variant = 'info',
  actions,
  onClick,
  className,
}: CalloutProps) {
  const isClickable = !!onClick;
  const cls = clsx(
    styles.root,
    VARIANT_CLASS[variant],
    isClickable && styles.rootClickable,
    className,
  );

  const content = (
    <>
      {Icon && (
        <span className={styles.icon}>
          <Icon size={18} strokeWidth={1.75} aria-hidden />
        </span>
      )}
      <div className={styles.body}>
        {title && <div className={styles.title}>{title}</div>}
        <div className={styles.text}>{children}</div>
      </div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </>
  );

  if (isClickable) {
    return (
      <button type="button" className={cls} onClick={onClick}>
        {content}
      </button>
    );
  }

  return <div className={cls}>{content}</div>;
}
